// One waitlist request, drawn as an appointment card.
//
// Deliberately the SAME card as `components/Appointments/Appointment.js`: the
// white 8px card with the gray.200 border, the status pill, the avatar row, the
// gray.50 band and the 11px actions. A waitlist request is the same kind of
// thing to a patient — a visit they are trying to get — so a second visual
// language on the same page reads as a second product.
//
// What differs is what the card CANNOT say: there is no date and no time,
// because nobody has one yet. The band that holds "Tuesday 3:00 PM" on an
// appointment holds when they are FREE, which is the whole content of a
// waitlist request and the reason it deserves the same prominence.

import {
  Avatar,
  Box,
  Button,
  Flex,
  Icon,
  Text,
} from "@chakra-ui/react";
import {FiClock, FiMapPin, FiCalendar} from "react-icons/fi";
import {describeBands} from "./describeBands";
import {formatMinutes} from "./serviceDurations";

/** How long they have been waiting, in the words a person would use. */
function waitedFor(createdAt) {
  const since = Number(createdAt);
  if (!Number.isFinite(since)) return "";
  const days = Math.floor((Date.now() - since) / 86_400_000);
  if (days < 1) return "Added today";
  if (days === 1) return "Waiting 1 day";
  if (days < 30) return `Waiting ${days} days`;
  const months = Math.round(days / 30);
  return months === 1 ? "Waiting about a month" : `Waiting about ${months} months`;
}

export default function WaitlistRequestCard({
  entry,
  serviceName,
  clinicianName,
  clinician,
  locationName,
  editable,
  onEdit,
  onLeave,
}) {
  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="8px"
      p={4}
      transition="all 0.15s ease"
      _hover={{borderColor: "gray.300", boxShadow: "sm"}}
    >
      {/* Row 1: what state this is in, and where */}
      <Flex align="center" justify="space-between" mb={3}>
        <Flex align="center" gap={2}>
          <Flex
            align="center"
            gap={1.5}
            px={2}
            py={0.5}
            borderRadius="full"
            bg="purple.50"
            w="fit-content"
          >
            <Box w="5px" h="5px" borderRadius="full" bg="#6772e5" />
            <Text fontSize="11px" fontWeight="500" color="purple.600">
              On the waitlist
            </Text>
          </Flex>
          <Flex align="center" gap={1} color="#6b7280">
            <Icon as={FiClock} fontSize="12px" />
            <Text fontSize="11px" fontWeight="500">
              {waitedFor(entry.createdAt)}
            </Text>
          </Flex>
        </Flex>

        {/* Named so two requests at different sites tell apart, which a service
            and an availability alone do not do. */}
        {locationName && (
          <Flex align="center" gap={1} color="#6b7280" minW={0}>
            <Icon as={FiMapPin} fontSize="12px" />
            <Text fontSize="11px" noOfLines={1}>
              {locationName}
            </Text>
          </Flex>
        )}
      </Flex>

      {/* Row 2: who, and for what */}
      <Flex align="center" gap={3} mb={3}>
        <Avatar
          size="sm"
          name={clinicianName}
          src={clinician?.photo}
          bg={entry.clinicianId ? "#6772e5" : "gray.300"}
          color="white"
          fontSize="xs"
        />
        <Box flex={1} minW={0}>
          <Text fontSize="13px" fontWeight="600" color="#1a1f36" noOfLines={1}>
            {/* `null` means ANY clinician and is the common case. Saying so
                beats a blank, and beats pretending somebody was chosen. */}
            {clinicianName}
          </Text>
          <Text fontSize="12px" color="#6b7280" noOfLines={1}>
            {serviceName}
          </Text>
        </Box>
      </Flex>

      {/* Row 3: where the date and time would be on an appointment. There is
          none, so it holds the availability — which is what this request IS. */}
      <Flex
        bg="gray.50"
        borderRadius="6px"
        px={3}
        py={2}
        mb={3}
        align="center"
        justify="space-between"
        gap={3}
      >
        <Flex align="center" gap={2} minW={0}>
          <Icon as={FiCalendar} fontSize="13px" color="#6b7280" />
          <Text fontSize="12px" fontWeight="500" color="#1a1f36" noOfLines={1}>
            {describeBands(entry.bands)}
          </Text>
        </Flex>
        {Number.isInteger(entry.requestedDurationMinutes) && (
          <Flex align="center" gap={2} flexShrink={0}>
            <Icon as={FiClock} fontSize="13px" color="#6b7280" />
            <Text fontSize="12px" fontWeight="500" color="#1a1f36">
              {formatMinutes(entry.requestedDurationMinutes)}
            </Text>
          </Flex>
        )}
      </Flex>

      {/* Row 4: the same 11px actions the appointment cards use */}
      <Flex gap={2} flexWrap="wrap" align="center">
        {editable ? (
          <Button
            size="xs"
            variant="ghost"
            color="#6b7280"
            _hover={{color: "#6772e5", bg: "#f0f0ff"}}
            borderRadius="6px"
            fontSize="11px"
            fontWeight="500"
            onClick={onEdit}
          >
            Change request
          </Button>
        ) : (
          // Reading and leaving survive the clinic switching patient requests
          // off; editing files a new request, so it does not. Nor is a request
          // tied to a booked visit changed here: it changes with that visit.
          // The words meet AA contrast (the token ink-2), as nothing else on the card may fall under.
          <Text fontSize="11px" color="var(--blh-ink-2)">
            Call the clinic to change this
          </Text>
        )}
        <Button
          size="xs"
          variant="ghost"
          color="#6b7280"
          _hover={{color: "#e53e3e", bg: "red.50"}}
          borderRadius="6px"
          fontSize="11px"
          fontWeight="500"
          onClick={onLeave}
        >
          Leave waitlist
        </Button>
      </Flex>
    </Box>
  );
}
